import { AlertTriangle } from 'react-feather';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  AddOrderMutation as AddOrder, GetOrdersForBackendQuery as GetOrdersForBackend, Permission,
} from '__generated__/graphql';

import { ExportsContext } from '../../Exports/ExportsProvider';
import { assetUrl, isEmpty, isGraphQLValidationError } from '../../../common/helpers';
import ExportOrdersForm from './ExportOrdersForm';
import GetOrdersForBackendQuery from './GetOrdersForBackendQuery';
import OrderFilters, {
  OrderFilterParams, OrderStats, mapOrderFiltersToServerInput, parseOrderFiltersUrlState,
} from './OrderFilters';
import ProtectedSection from '../../Common/ProtectedSection';
import StatusLabels from '../Participant/Orders/StatusLabels';
import UI from '../../../common/UI';
import config from '../../config';
import useAddOrderModal from '../Participant/Orders/useAddOrderModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useUrlState from '../../../common/useUrlState';

type Order = GetOrdersForBackend['project']['orders']['data'][0];

const OrdersPage = () => {
  const { t } = useTranslation();
  const history = useHistory<{ order_filters?: OrderFilterParams; }>();
  const project = useProject();
  const device = useMediaDevice();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('orders'));

  const [filtersKey, setFiltersKey] = useState(0);

  const [{ page, ...urlFilters }, setUrlState] = useUrlState({
    parse: (params, { integer }) => ({
      ...parseOrderFiltersUrlState(params),
      page: integer(params.page, 1),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Orders' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  const filters = {
    ...urlFilters,
    event: recentEvent.id,
  };

  const handleFiltersChange = useCallback((newFilters: OrderFilterParams) => {
    setUrlState(() => ({
      ...newFilters,
      page: null,
    }), true);
  }, [setUrlState]);

  // State that contains all filter stats
  const [filterStats, setFilterStats] = useState<OrderStats>();

  const ordersCount = filterStats?.orders || 0;
  const purchasesCount = filterStats?.purchases || 0;
  const enableExport = ordersCount + purchasesCount > 0 && (
    ordersCount < config.maxExportSize.orders || purchasesCount < config.maxExportSize.purchases
  );

  /** Filters object that can be sent to the server */
  const orderFilters = mapOrderFiltersToServerInput(filters);

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((filter) => !isEmpty(filter));

  // Query (and filter) orders.
  const { data, loading } = useQuery(
    GetOrdersForBackendQuery,
    {
      variables: {
        ...orderFilters,
        project_id: project.id,
        page,
        per_page: 20,
      },
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Recent event no longer exists, reset it.
          recentEvent.reset();
        }
      },
    },
  );

  const [openAddOrderModal] = useAddOrderModal({
    eventId: filters.event,
    onSuccess: (order: AddOrder['addOrder']) => {
      history.push(`/${project.id}/participants/${order.participant.id}/orders/${order.id}`);
    },
  });

  const { openExportsModal } = useContext(ExportsContext);

  const [openExportOrdersModal, closeExportOrdersModal] = useModal(() => (
    <UI.Modal maxWidth={800} close={closeExportOrdersModal}>
      <ExportOrdersForm
        filters={orderFilters}
        ordersCount={ordersCount}
        purchasesCount={purchasesCount}
        onSuccess={() => { closeExportOrdersModal(); openExportsModal(); }}
      />
    </UI.Modal>
  ), [orderFilters, ordersCount, purchasesCount]);

  const events = data?.project.events;
  const orders = data?.project.orders;

  const [showEvent, setShowEvent] = useState(!filters.event);
  const ordersRef = useRef(orders);

  // Show event labels after new orders have been retrieved.
  useEffect(() => {
    if (ordersRef.current !== orders) {
      setShowEvent(!filters.event);
    }
    ordersRef.current = orders;
  }, [orders, filters.event]);

  if (!events) {
    return <UI.PageLoader />;
  }

  return (
    <>
      {events.total > 0 && (
        <UI.Section>
          <UI.GridContainer>
            <UI.GridContainer sc={{ columns: '1fr 3fr' }}>
              <UI.SectionHeading>
                {t('orders')}
              </UI.SectionHeading>
              <UI.FlexContainer sc={{ justifyContent: 'end', spaceX: 1 }}>
                <ProtectedSection permissions={[Permission.edit_registrations]}>
                  <UI.Button sc={{ brand: 'secondary' }} onClick={openAddOrderModal}>
                    {t('add_order')}
                  </UI.Button>
                </ProtectedSection>
                {!device.isSmall && (
                  <ProtectedSection permissions={[Permission.exports]}>
                    <UI.TooltipContainer>
                      {ordersCount > config.maxExportSize.orders && (
                        <UI.Tooltip sc={{ alignH: 'center', alignV: 'bottom' }}>
                          <UI.Icon>
                            <AlertTriangle />
                          </UI.Icon>
                          {' '}
                          {t('export_too_large')}
                        </UI.Tooltip>
                      )}
                      <UI.Button
                        onClick={() => openExportOrdersModal()}
                        disabled={!enableExport}
                      >
                        {t('export')}
                      </UI.Button>
                    </UI.TooltipContainer>
                  </ProtectedSection>
                )}
              </UI.FlexContainer>
            </UI.GridContainer>

            <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
              <UI.Div>
                <OrderFilters
                  filters={filters}
                  onChange={handleFiltersChange}
                  onStatsChange={setFilterStats}
                  key={filtersKey}
                />
              </UI.Div>

              <UI.Div>
                <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
                  {orders.data.map((order) => (
                    <OrderCard order={order} showEvent={showEvent} key={order.id} />
                  ))}
                  {orders.data.length === 0 && (
                    <>
                      {hasFilters && (
                        <UI.Card
                          sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                          style={{ textWrap: 'balance' }}
                        >
                          <UI.Div>
                            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                          </UI.Div>
                          {t('no_orders_match_filters')}
                          {' '}
                          <UI.Link to={`/${project.id}/participants/orders`}>{t('reset_filters')}</UI.Link>
                        </UI.Card>
                      )}

                      {!hasFilters && !loading && (
                        <UI.Card
                          sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                          style={{ textWrap: 'balance' }}
                        >
                          <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                          <UI.H3 sc={{ mb: 2 }}>{t('no_orders_yet')}</UI.H3>
                          <UI.Paragraph sc={{ mb: 0 }}>
                            {t('no_orders_get_started')}
                          </UI.Paragraph>
                        </UI.Card>
                      )}
                    </>
                  )}
                </UI.GridContainer>

                {(page > 1 || orders.has_more_pages) && (
                  <UI.Div sc={{ mt: 4 }}>
                    <UI.Pager disableNext={!orders.has_more_pages} />
                  </UI.Div>
                )}
              </UI.Div>
            </UI.GridContainer>
          </UI.GridContainer>
        </UI.Section>
      )}

      {events.total === 0 && (
        <UI.Section sc={{ compact: true, mt: { md: 8 } }}>
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
            <UI.H3 sc={{ mb: 2 }}>{t('no_orders_yet')}</UI.H3>
            <UI.Paragraph>{t('no_orders_because_no_events')}</UI.Paragraph>
            <UI.NavButton to={`/${project.id}/events/add`} sc={{ brand: 'secondary' }}>
              {t('create_an_event')}
            </UI.NavButton>
          </UI.Card>
        </UI.Section>
      )}
    </>
  );
};

interface OrderCardProps {
  order: Order;
  showEvent?: boolean;
}

const OrderCard = ({ order, showEvent }: OrderCardProps) => {
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const project = useProject();
  const device = useMediaDevice();

  const to = `/${project.id}/participants/${order.participant.id}/orders/${order.id}`;

  return (
    <UI.NavCard to={to}>
      <UI.GridContainer
        sc={{ alignVertical: 'center', columns: ['3fr 1fr 1fr', '4fr 4fr 2fr 3fr 80px'], gutter: 0.5 }}
      >
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4>
            <UI.Truncate>
              {order.company_name || order.participant.full_name}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        {!device.isSmall && (
          <>
            <UI.FlexContainer sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
              {showEvent ? (
                <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
                  <UI.Truncate>
                    {order.event.title}
                  </UI.Truncate>
                </UI.Label>
              ) : (
                <UI.Truncate>
                  {order.invoice_id}
                </UI.Truncate>
              )}
            </UI.FlexContainer>
            <UI.Div sc={{ color: 'gray.500' }}>
              {formatCurrency(order.amount)}
            </UI.Div>
          </>
        )}
        <UI.Div>
          <StatusLabels
            amount={order.amount}
            remainingAmount={order.remaining_amount}
            refundedAmount={order.refunded_amount}
            balance={order.balance}
            cancelled={order.cancelled}
            waitlist={order.waitlist}
            sc={{ my: -0.5 }}
          />
        </UI.Div>
        <UI.Div sc={{ small: true, color: 'gray.500', textAlign: 'right' }}>
          {formatDate(
            parseDate(order.invoice_date, { format: 'internal_date' }),
            { format: 'display_compact_date' },
          )}
        </UI.Div>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

export default OrdersPage;
